import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Search } from 'lucide-react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useDebounce } from 'use-debounce';

import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { useToast } from '@/hooks/use-toast';
import { sendMail } from '@/components/core/mail/services/mail.service';
import {
  getContractDocumentById,
  updateContractDocument,
} from '@/modules/org/contracts/services/contract-document.service';
import { getOrgMembers } from '@/modules/org/org-users/services/org-users.service';
import { orgContractDocumentByIdQueryKey } from '@/graphql';
import { useCurrentOrgId } from '@/store/use-my-oganization-store';
import type { OrgMember } from '@/modules/org/org-users/types/org-users.types';

const REVIEWER_PAGE_SIZE = 20;

const projectKey = import.meta.env.VITE_X_BLOCKS_KEY ?? '';
const legalTemplateBaseUrl = (import.meta.env.VITE_LEGAL_TEMPLATE_BASE_URL ?? '').replace(/\/$/, '');

interface SendForReviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  templateSlug: string;
  documentId: string | null;
}

export function SendForReviewDialog({
  open,
  onOpenChange,
  onConfirm,
  templateSlug,
  documentId,
}: SendForReviewDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const orgId = useCurrentOrgId();
  const reviewerNameInputId = useId();
  const [reviewerName, setReviewerName] = useState('');
  const [selectedReviewer, setSelectedReviewer] = useState<OrgMember | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [userLookupError, setUserLookupError] = useState('');
  const [isCheckingUser, setIsCheckingUser] = useState(false);
  const [debouncedName] = useDebounce(reviewerName.trim(), 300);

  const {
    data: reviewerPages,
    isFetching: isFetchingReviewers,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ['sendForReviewMembers', orgId, debouncedName],
    queryFn: ({ pageParam }) =>
      getOrgMembers(orgId!, {
        page: pageParam,
        pageSize: REVIEWER_PAGE_SIZE,
        name: debouncedName,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce((count, page) => count + page.members.length, 0);
      return loadedCount < lastPage.totalCount ? allPages.length : undefined;
    },
    enabled: open && !!orgId && debouncedName.length > 0,
    staleTime: 30 * 1000,
  });

  const reviewerOptions = useMemo(
    () => reviewerPages?.pages.flatMap((page) => page.members) ?? [],
    [reviewerPages]
  );
  const isWaitingForSearch = debouncedName !== reviewerName.trim();
  const isSearching = isWaitingForSearch || (isFetchingReviewers && !isFetchingNextPage);

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      setReviewerName('');
      setSelectedReviewer(null);
      setIsDropdownOpen(false);
      setUserLookupError('');
      setIsCheckingUser(false);
    }
  };

  const handleConfirm = async () => {
    if (!selectedReviewer) return;

    setUserLookupError('');
    setIsCheckingUser(true);

    try {
      if (!orgId) {
        setUserLookupError(
          t('GENERATEREVIEW_REVIEWER_ORG_NOT_FOUND', {
            defaultValue: 'Unable to determine the current organization.',
          })
        );
        return;
      }

      if (!documentId) {
        setUserLookupError(
          t('GENERATEREVIEW_REVIEWER_DOCUMENT_NOT_FOUND', {
            defaultValue: 'Unable to determine the document to review.',
          })
        );
        return;
      }

      const reviewer = selectedReviewer;

      const contractDocument = await getContractDocumentById(documentId);
      if (!contractDocument) {
        throw new Error('Contract document not found');
      }
      const reviewers = [
        ...(contractDocument.Reviewers ?? []).filter(
          (email) => email.trim().toLowerCase() !== reviewer.email.trim().toLowerCase()
        ),
        reviewer.email,
      ];

      await Promise.all([
        sendMail({
          to: [reviewer.email],
          purpose: 'ReviewDocument',
          language: 'en-US',
          bodyDataContext: {
            DisplayName: `${reviewer.firstName ?? ''} ${reviewer.lastName ?? ''}`.trim(),
            ReviewDocumentUrl: `${legalTemplateBaseUrl}/templates/${encodeURIComponent(templateSlug)}/review/approval?document=${encodeURIComponent(documentId)}`,
          },
          projectKey,
        }),
        updateContractDocument(documentId, {
          HasSentForApproval: true,
          Reviewers: reviewers,
        }),
      ]);

      void queryClient.invalidateQueries({
        queryKey: orgContractDocumentByIdQueryKey(documentId),
      });

      handleOpenChange(false);
      toast({
        variant: 'success',
        title: t('GENERATEREVIEW_SENT_FOR_REVIEW', {
          defaultValue: 'Sent for review',
        }),
      });
    } catch (error) {
      console.error('Failed to send document for review', error);
      setUserLookupError(
        t('GENERATEREVIEW_REVIEWER_LOOKUP_FAILED', {
          defaultValue: 'Unable to send the document for review. Please try again.',
        })
      );
    } finally {
      setIsCheckingUser(false);
    }
  };

  const handleSkip = () => {
    handleOpenChange(false);
    onConfirm();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="w-[calc(100%-2rem)] sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('GENERATEREVIEW_ACTIONS_SENDFORREVIEW', { defaultValue: 'Send for Review' })}
          </DialogTitle>
          <DialogDescription>
            {t('GENERATEREVIEW_SENDFORREVIEW_CONFIRMATION', {
              defaultValue:
                'Sends the current document to a reviewer. They can review or make any changes to the document if necessary.',
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="relative space-y-2">
          <Label htmlFor={reviewerNameInputId}>
            {t('GENERATEREVIEW_REVIEWER_NAME_LABEL', { defaultValue: 'Reviewer name' })}
          </Label>
          <Input
            id={reviewerNameInputId}
            type="text"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={isDropdownOpen}
            aria-controls={`${reviewerNameInputId}-options`}
            value={reviewerName}
            onChange={(event) => {
              setReviewerName(event.target.value);
              setSelectedReviewer(null);
              setIsDropdownOpen(true);
              setUserLookupError('');
            }}
            onFocus={() => setIsDropdownOpen(true)}
            placeholder={t('GENERATEREVIEW_REVIEWER_NAME_PLACEHOLDER', {
              defaultValue: 'Search by name',
            })}
            autoComplete="off"
            aria-invalid={!!userLookupError}
            aria-describedby={userLookupError ? `${reviewerNameInputId}-error` : undefined}
            required
          />

          {isDropdownOpen && reviewerName.trim() && !selectedReviewer && (
            <div
              id={`${reviewerNameInputId}-options`}
              role="listbox"
              className="absolute z-600 mt-1 w-full overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
            >
              <div
                className="max-h-56 overflow-y-auto p-1"
                onScroll={(event) => {
                  const target = event.currentTarget;
                  const isNearBottom =
                    target.scrollHeight - target.scrollTop - target.clientHeight < 32;
                  if (isNearBottom && hasNextPage && !isFetchingNextPage) {
                    void fetchNextPage();
                  }
                }}
              >
                {isSearching ? (
                  <div className="flex items-center justify-center gap-2 px-3 py-6 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t('GENERATEREVIEW_REVIEWER_SEARCHING', { defaultValue: 'Searching users...' })}
                  </div>
                ) : reviewerOptions.length === 0 ? (
                  <div className="flex flex-col items-center gap-2 px-3 py-6 text-center text-sm text-muted-foreground">
                    <Search className="h-4 w-4" />
                    {t('GENERATEREVIEW_REVIEWER_NO_RESULTS', {
                      defaultValue: 'No organization users match this name.',
                    })}
                  </div>
                ) : (
                  reviewerOptions.map((member) => {
                    const displayName = `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim();
                    return (
                      <button
                        key={member.id}
                        type="button"
                        role="option"
                        aria-selected={false}
                        className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left hover:bg-accent focus:bg-accent focus:outline-none"
                        onClick={() => {
                          setSelectedReviewer(member);
                          setReviewerName(displayName || member.email);
                          setIsDropdownOpen(false);
                          setUserLookupError('');
                        }}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{displayName || member.email}</p>
                          {displayName && (
                            <p className="truncate text-xs text-muted-foreground">{member.email}</p>
                          )}
                        </div>
                      </button>
                    );
                  })
                )}
                {isFetchingNextPage && (
                  <div className="flex justify-center py-2" aria-label="Loading more users">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                )}
              </div>
            </div>
          )}

          {userLookupError && (
            <p id={`${reviewerNameInputId}-error`} className="text-sm text-destructive">
              {userLookupError}
            </p>
          )}
        </div>
        <DialogFooter className="gap-2 sm:grid sm:grid-cols-2 sm:space-x-0">
          <Button
            variant="outline"
            onClick={handleSkip}
            disabled={isCheckingUser}
            className="h-auto min-h-11 min-w-0 whitespace-normal px-3 text-center leading-tight"
          >
            {t('GENERATEREVIEW_ACTIONS_SENDFORSIGNATURE', { defaultValue: 'Send for Signature' })}
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleConfirm()}
            disabled={!selectedReviewer || isCheckingUser}
            className="h-auto min-h-11 min-w-0 whitespace-normal px-3 text-center leading-tight"
          >
            {isCheckingUser && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {t('GENERATEREVIEW_ACTIONS_SENDFORREVIEW', { defaultValue: 'Send for Review' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
